import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye } from "lucide-react";
import { imgUrl, formatPrice } from "@/lib/api";

export const ProductCard = ({ product, onQuickView, index = 0 }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [style, setStyle] = useState({});

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(900px) rotateY(${px * 8}deg) rotateX(${-py * 8}deg) scale(1.02)`,
    });
  };
  const reset = () => setStyle({ transform: "perspective(900px) rotateY(0) rotateX(0) scale(1)" });

  const price = product.discountPrice || product.price;
  const hasDiscount = product.discountPrice && product.discountPrice < product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={style}
        className="tilt-card relative aspect-[3/4] overflow-hidden bg-[#1A1D24] cursor-pointer"
        onClick={() => navigate(`/product/${product.id}`)}
      >
        <img
          src={imgUrl(product.image || product.images?.[0])}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.isNewArrival && !product.isSoldOut && (
            <span className="bg-white text-black text-[10px] font-mono-th uppercase tracking-widest px-2 py-1">New</span>
          )}
          {hasDiscount && !product.isSoldOut && (
            <span className="bg-amber-600 text-white text-[10px] font-mono-th uppercase tracking-widest px-2 py-1">Sale</span>
          )}
        </div>
        {product.isSoldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="border border-rose-500 text-rose-400 font-mono-th text-xs uppercase tracking-[0.3em] px-4 py-2">Sold Out</span>
          </div>
        )}

        {onQuickView && !product.isSoldOut && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickView(product);
            }}
            className="absolute bottom-3 left-3 right-3 glass text-white py-3 font-mono-th text-[11px] uppercase tracking-[0.2em] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2"
            data-testid={`quick-view-btn-${product.id}`}
          >
            <Eye className="w-4 h-4" /> Quick View
          </button>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-neutral-500">{product.category}</p>
          <h3 className="font-display font-medium text-sm md:text-base mt-1 leading-tight line-clamp-2">{product.name}</h3>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display font-semibold text-sm md:text-base">{formatPrice(price)}</p>
          {hasDiscount && (
            <p className="text-neutral-600 text-xs line-through">{formatPrice(product.price)}</p>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(product.sizes || []).slice(0, 5).map((s) => (
          <span key={s} className="font-mono-th text-[10px] text-neutral-500 border border-white/10 px-1.5 py-0.5">{s}</span>
        ))}
      </div>
    </motion.div>
  );
};
